import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  XMarkIcon,
  CalendarIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  TrashIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  PlusIcon,
  InformationCircleIcon,
  BellIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';
import { getUserTimezone, formatDateTimeInTimezone } from '../services/timezoneUtils';
import type { ClientAppointment } from '../services/pracmagicTypes';
import type { AppointmentPaymentStatus } from '../services/paymentService';
import SessionSummaryPanel from './SessionSummaryPanel';
import CheckInsTab from './CheckInsTab';
import ActionItemsPanel from './ActionItemsPanel';
import FollowUpsTab from './FollowUpsTab';
import { Spinner } from './ui';
import { type ApiCountry } from './AddressFields';
import { useCountryStates } from '../hooks/useCountryStates';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  listClientFiles, ClientFolderPacket,
  listDocuments, DocumentItem, createPacket,
  listTemplateTags, DocumentTemplateTag,
} from '../services/documentService';

export interface ClientDetailClient {
  client_id: string;
  relationship_id: string;
  name: string;
  email: string;
  status: 'active' | 'inactive' | 'new';
  start_date: string | null;
  onboarding_step?: 'invited' | 'registered' | 'packet_sent' | 'active';
  ai_consent: boolean | null;
}

type DetailTab = 'profile' | 'schedules' | 'checkins' | 'followups' | 'action-items' | 'ai-notes' | 'agreements';

interface Props {
  client: ClientDetailClient;
  coachId: string;
  onClose: () => void;
  onSchedule: () => void;
  onDeleted: () => void;
  apptPaymentStatuses?: Record<string, AppointmentPaymentStatus>;
  onProcessPayment?: (appointmentId: string) => void;
  inline?: boolean;
}

// ── AI Notes config types ─────────────────────────────────────────────────────

interface ExtraSection {
  id: string;
  display_name: string;
  instructions: string;
  enabled: boolean;
  client_visible: boolean;
}

// ── Profile field types ───────────────────────────────────────────────────────

interface ClientProfile {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  street_address: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  marital_status: string;
  dependents: string;
  occupation: string;
  employer: string;
  industry: string;
  bio: string;
  start_date: string | null;
  ai_consent: boolean | null;
  ai_consent_at: string | null;
  privacy_policy_accepted: boolean | null;
  privacy_policy_accepted_at: string | null;
  terms_accepted: boolean | null;
  terms_accepted_at: string | null;
}

const EMPTY_PROFILE: ClientProfile = {
  first_name: '', last_name: '', email: '', phone: '', date_of_birth: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  street_address: '', city: '', state_province: '', postal_code: '', country: '',
  marital_status: '', dependents: '', occupation: '', employer: '', industry: '',
  bio: '', start_date: null,
  ai_consent: null, ai_consent_at: null,
  privacy_policy_accepted: null, privacy_policy_accepted_at: null,
  terms_accepted: null, terms_accepted_at: null,
};

// ── Field row component ───────────────────────────────────────────────────────

const Field: React.FC<{
  label: string;
  value: string;
  field: keyof ClientProfile;
  type?: string;
  multiline?: boolean;
  onChange: (field: keyof ClientProfile, value: string) => void;
}> = ({ label, value, field, type = 'text', multiline = false, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
      {label}
    </label>
    {multiline ? (
      <textarea
        rows={3}
        value={value}
        onChange={e => onChange(field, e.target.value)}
        className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={e => onChange(field, e.target.value)}
        className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
      />
    )}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const ClientDetailPanel: React.FC<Props> = ({
  client, coachId, onClose, onSchedule, onDeleted,
  apptPaymentStatuses = {}, onProcessPayment, inline = false,
}) => {
  const [tab, setTab] = useState<DetailTab>('schedules');
  const [appointments, setAppointments] = useState<ClientAppointment[]>([]);
  const [loadingAppts, setLoadingAppts] = useState(true);
  const [summaryAppt, setSummaryAppt] = useState<ClientAppointment | null>(null);
  const [hasActiveTracker, setHasActiveTracker] = useState<boolean | null>(null);
  const timezone = getUserTimezone();

  // Profile state
  const [profile, setProfile] = useState<ClientProfile>(EMPTY_PROFILE);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [apiCountries, setApiCountries] = useState<ApiCountry[]>([]);
  // DB-backed state/province options for the selected country (E1.8 / CSECR-64).
  const { states: stateOptions } = useCountryStates(profile.country);
  const [profileDirty, setProfileDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete state
  const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm'>('idle');
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Transcript upload state
  const [transcriptAppt, setTranscriptAppt] = useState<ClientAppointment | null>(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [transcriptError, setTranscriptError] = useState('');
  const [transcriptDone, setTranscriptDone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Agreements tab state
  const [agreementPackets, setAgreementPackets] = useState<ClientFolderPacket[]>([]);
  const [agreementsLoading, setAgreementsLoading] = useState(false);
  const [agreementsError, setAgreementsError] = useState<string | null>(null);
  const [sendWelcomeModal, setSendWelcomeModal] = useState(false);
  const [welcomeDocs, setWelcomeDocs] = useState<DocumentItem[]>([]);
  const [welcomeTitle, setWelcomeTitle] = useState('Welcome Packet');
  const [welcomeSelectedIds, setWelcomeSelectedIds] = useState<Set<string>>(new Set());
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [welcomeSendError, setWelcomeSendError] = useState('');
  const [welcomeSendSuccess, setWelcomeSendSuccess] = useState('');
  const [welcomeCustomTags, setWelcomeCustomTags] = useState<DocumentTemplateTag[]>([]);
  const [welcomeTagValues, setWelcomeTagValues] = useState<Record<string, string>>({});

  // AI Notes config state
  const [extraSections, setExtraSections] = useState<ExtraSection[]>([]);
  const [aiConfigLoading, setAiConfigLoading] = useState(false);
  const [aiConfigSaving, setAiConfigSaving] = useState(false);
  const [aiConfigSaved, setAiConfigSaved] = useState(false);
  const [aiConfigError, setAiConfigError] = useState<string | null>(null);

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
  }), []);

  // Load appointments + check for active tracker
  useEffect(() => {
    fetch(`${config.apiBaseUrl}${endpoints.main.clients.sessions(client.client_id)}`, {
      headers: authHeaders(),
    })
      .then(r => r.json())
      .then(data => setAppointments(data.appointments || []))
      .catch(() => setAppointments([]))
      .finally(() => setLoadingAppts(false));

    fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.byClient(client.client_id)}`, {
      headers: authHeaders(),
    })
      .then(r => r.json())
      .then(data => {
        const qs: any[] = data.questionnaires || [];
        setHasActiveTracker(qs.some(q => q.is_active));
      })
      .catch(() => setHasActiveTracker(false));
  }, [client.client_id, authHeaders]);

  // Load full profile when profile tab selected
  useEffect(() => {
    if (tab !== 'profile') return;
    setLoadingProfile(true);
    setProfileDirty(false);
    setSaveError(null);
    Promise.all([
      fetch(`${config.apiBaseUrl}/countries`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${config.apiBaseUrl}/coaches/${coachId}/clients/${client.client_id}/profile`, { headers: authHeaders() }).then(r => r.json()),
    ]).then(([countries, data]) => {
        setApiCountries(countries);
        setProfile({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          date_of_birth: data.date_of_birth || '',
          emergency_contact_name: data.emergency_contact_name || '',
          emergency_contact_phone: data.emergency_contact_phone || '',
          street_address: data.street_address || '',
          city: data.city || '',
          state_province: data.state_province || '',
          postal_code: data.postal_code || '',
          country: data.country || '',
          marital_status: data.marital_status || '',
          dependents: data.dependents || '',
          occupation: data.occupation || '',
          employer: data.employer || '',
          industry: data.industry || '',
          bio: data.bio || '',
          start_date: data.start_date || null,
          ai_consent: data.ai_consent ?? null,
          ai_consent_at: data.ai_consent_at ?? null,
          privacy_policy_accepted: data.privacy_policy_accepted ?? null,
          privacy_policy_accepted_at: data.privacy_policy_accepted_at ?? null,
          terms_accepted: data.terms_accepted ?? null,
          terms_accepted_at: data.terms_accepted_at ?? null,
        });
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, [tab, client.client_id, coachId, authHeaders]);

  // Load agreements when agreements tab selected
  useEffect(() => {
    if (tab !== 'agreements') return;
    setAgreementsLoading(true);
    setAgreementsError(null);
    listClientFiles(Number(client.relationship_id))
      .then(setAgreementPackets)
      .catch(() => setAgreementsError('Could not load agreements.'))
      .finally(() => setAgreementsLoading(false));
  }, [tab, client.relationship_id]);

  // Load AI Notes config when ai-notes tab selected
  useEffect(() => {
    if (tab !== 'ai-notes') return;
    setAiConfigLoading(true);
    setAiConfigError(null);
    fetch(`${config.apiBaseUrl}${endpoints.main.coaches.clientAiConfig(Number(client.relationship_id))}`, {
      headers: authHeaders(),
    })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => setExtraSections(data.extra_sections || []))
      .catch(() => setAiConfigError('Could not load AI config.'))
      .finally(() => setAiConfigLoading(false));
  }, [tab, client.relationship_id, authHeaders]);

  const addExtraSection = () => {
    setExtraSections(prev => [
      ...prev,
      { id: crypto.randomUUID(), display_name: '', instructions: '', enabled: true, client_visible: false },
    ]);
  };

  const updateExtraSection = (id: string, field: keyof ExtraSection, value: string | boolean) => {
    setExtraSections(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
    setAiConfigSaved(false);
    setAiConfigError(null);
  };

  const removeExtraSection = (id: string) => {
    setExtraSections(prev => prev.filter(s => s.id !== id));
    setAiConfigSaved(false);
  };

  const saveAiConfig = async () => {
    setAiConfigSaving(true);
    setAiConfigError(null);
    setAiConfigSaved(false);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.clientAiConfig(Number(client.relationship_id))}`,
        { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ extra_sections: extraSections }) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setAiConfigError((err as any).detail || 'Save failed.');
      } else {
        const data = await res.json();
        setExtraSections(data.extra_sections || []);
        setAiConfigSaved(true);
        setTimeout(() => setAiConfigSaved(false), 3000);
      }
    } catch {
      setAiConfigError('Network error. Please try again.');
    } finally {
      setAiConfigSaving(false);
    }
  };

  const handleFieldChange = (field: keyof ClientProfile, value: string) => {
    setProfile(p => ({ ...p, [field]: value }));
    setProfileDirty(true);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSaveProfile = async () => {
    if (profile.phone && !isValidPhoneNumber(profile.phone)) {
      setPhoneError('Enter a valid phone number');
      return;
    }
    setPhoneError(null);
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { email, start_date, ai_consent_at, privacy_policy_accepted_at, terms_accepted_at, ...editable } = profile;
      const payload: Record<string, string | boolean | null> = {};
      for (const [k, v] of Object.entries(editable)) {
        payload[k] = typeof v === 'boolean' ? v : (v || null);
      }
      const res = await fetch(
        `${config.apiBaseUrl}/coaches/${coachId}/clients/${client.client_id}/profile`,
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(payload) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError((err as any).detail || 'Save failed');
      } else {
        setSaveSuccess(true);
        setProfileDirty(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch {
      setSaveError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== client.name) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/coaches/${coachId}/clients/${client.client_id}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (res.ok || res.status === 204) {
        onDeleted();
        onClose();
      } else {
        const body = await res.json().catch(() => ({}));
        setDeleteError(body.detail || `Error ${res.status}: failed to delete client`);
        setDeleting(false);
      }
    } catch {
      setDeleteError('Network error — please try again');
      setDeleting(false);
    }
  };

  const handleTranscriptFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setTranscriptText((ev.target?.result as string) || '');
    reader.readAsText(file);
  };

  const handleTranscriptSubmit = async () => {
    if (!transcriptAppt || !transcriptText.trim()) return;
    setTranscribing(true);
    setTranscriptError('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.session.summarize(transcriptAppt.id)}`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ transcript_text: transcriptText.trim() }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTranscriptError((data as any).detail || 'Summary generation failed');
      } else {
        setTranscriptDone(true);
        // Mark appointment as having a summary so the notes icon appears
        setAppointments(prev =>
          prev.map(a => a.id === transcriptAppt.id ? { ...a, has_summary: true } : a)
        );
      }
    } catch {
      setTranscriptError('Network error — please try again');
    } finally {
      setTranscribing(false);
    }
  };

  const now = new Date().toISOString();
  const upcoming = appointments.filter(a => a.scheduled_at > now && a.status !== 'completed' && a.status !== 'cancelled');
  const past     = appointments.filter(a => (a.scheduled_at <= now || a.status === 'completed') && a.status !== 'cancelled').reverse();

  const statusColor = client.status === 'active'
    ? 'bg-green-100 text-green-700'
    : client.status === 'new'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-gray-100 text-gray-500';

  const ApptRow = ({ appt }: { appt: ClientAppointment }) => {
    return (
      <div className="flex items-center justify-between py-3 px-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{appt.meeting_name}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {formatDateTimeInTimezone(appt.scheduled_at, timezone, {
              weekday: 'short', month: 'short', day: 'numeric',
              hour: 'numeric', minute: '2-digit',
            })}
            {' · '}{appt.duration_minutes} min
          </p>
        </div>
        <div className="ml-3 flex-shrink-0 flex items-center gap-1.5">
          <button
            onClick={() => setSummaryAppt(appt)}
            title={appt.has_summary ? 'View session notes' : 'Open session panel (add notes, action items, nudges)'}
            className={`p-1.5 rounded-lg transition-colors ${appt.has_summary ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'}`}
          >
            <DocumentTextIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setTranscriptAppt(appt); setTranscriptText(''); setTranscriptError(''); setTranscriptDone(false); }}
            title="Upload transcript to generate summary"
            className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {!inline && <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />}

      <div className={inline
        ? 'h-full flex flex-col bg-white border-l border-gray-200 overflow-hidden'
        : 'fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col'
      }>
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-primary-700">
                {client.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </span>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{client.name}</h2>
              <p className="text-xs text-gray-500">{client.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-3">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
              {client.status}
            </span>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-1">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── Onboarding status strip (hidden when active) ── */}
        {client.onboarding_step && client.onboarding_step !== 'active' && (
          <div className="mx-5 mt-3 mb-0 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              {(['invited', 'registered', 'packet_sent', 'active'] as const).map((step, i) => (
                <React.Fragment key={step}>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    client.onboarding_step === step
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    {step === 'invited' ? 'Invited' : step === 'registered' ? 'Registered' : step === 'packet_sent' ? 'Packet Sent' : 'Active'}
                  </span>
                  {i < 3 && <span className="text-gray-300 text-xs">›</span>}
                </React.Fragment>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-amber-700">
                {client.onboarding_step === 'invited' && 'Waiting for client to register via setup link.'}
                {client.onboarding_step === 'registered' && 'Client registered — send their Welcome Packet to complete onboarding.'}
                {client.onboarding_step === 'packet_sent' && 'Welcome Packet sent — waiting for client signature.'}
              </p>
              {client.onboarding_step === 'registered' && (
                <button
                  onClick={async () => {
                    setSendWelcomeModal(true);
                    setWelcomeTitle('Welcome Packet');
                    setWelcomeSelectedIds(new Set());
                    setWelcomeSendError('');
                    setWelcomeSendSuccess('');
                    const [docs, tags] = await Promise.all([
                      listDocuments().catch(() => [] as DocumentItem[]),
                      listTemplateTags().catch(() => [] as DocumentTemplateTag[]),
                    ]);
                    setWelcomeDocs(docs);
                    setWelcomeCustomTags(tags);
                    setWelcomeTagValues(Object.fromEntries(tags.map(t => [t.key, t.default_value])));
                  }}
                  className="flex-shrink-0 px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg"
                >
                  Send Welcome Packet →
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-5 flex-shrink-0">
          {([
            { key: 'schedules',    label: 'Schedules',    Icon: CalendarIcon },
            { key: 'checkins',     label: 'Check-ins',   Icon: ClipboardDocumentCheckIcon },
            { key: 'action-items', label: 'Action Items', Icon: ClipboardDocumentListIcon },
            { key: 'followups',    label: 'Follow Ups',  Icon: BellIcon },
            { key: 'profile',      label: 'Profile',     Icon: UserIcon },
            { key: 'ai-notes',     label: 'Note Config', Icon: SparklesIcon },
            { key: 'agreements',   label: 'Agreements',  Icon: DocumentTextIcon },
          ] as const).map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 py-3 px-1 mr-5 text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* ── Schedules tab ── */}
          {tab === 'schedules' && (
            <div className="p-5 space-y-6">
              {client.status === 'active' && (
                <>
                  <div className="flex items-center justify-between gap-3">
                    {hasActiveTracker === false && (
                      <button
                        onClick={() => setTab('checkins')}
                        className="text-xs text-violet-600 hover:text-violet-800 hover:underline transition-colors"
                      >
                        Set up tracker →
                      </button>
                    )}
                    <div className="ml-auto">
                      {client.ai_consent === null ? (
                        <div className="text-right">
                          <button disabled className="text-sm bg-gray-300 text-gray-500 px-4 py-2 rounded-lg font-medium cursor-not-allowed">
                            + Schedule Session
                          </button>
                          <p className="text-xs text-amber-600 mt-1">AI consent pending</p>
                        </div>
                      ) : (
                        <button
                          onClick={onSchedule}
                          className="text-sm bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                          + Schedule Session
                        </button>
                      )}
                    </div>
                  </div>
                  {client.ai_consent === false && (
                    <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-700">
                        <span className="font-semibold">Client has declined the AI usage policy.</span>{' '}
                        Notes will not be generated for sessions. Coach is responsible for manually capturing notes.
                      </p>
                    </div>
                  )}
                </>
              )}
              {loadingAppts ? (
                <Spinner size="md" className="py-10" />
              ) : (
                <>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Upcoming ({upcoming.length})
                    </p>
                    {upcoming.length === 0 ? (
                      <p className="text-sm text-gray-400 italic px-1">No upcoming sessions.</p>
                    ) : (
                      <div className="space-y-2">
                        {upcoming.map(a => <ApptRow key={a.id} appt={a} />)}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Past ({past.length})
                    </p>
                    {past.length === 0 ? (
                      <p className="text-sm text-gray-400 italic px-1">No past sessions yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {past.map(a => <ApptRow key={a.id} appt={a} />)}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Check-ins tab ── */}
          {tab === 'checkins' && (
            <div className="p-5">
              <CheckInsTab clientId={client.client_id} coachClientId={client.relationship_id} isCoach />
            </div>
          )}

          {/* ── Action Items tab ── */}
          {tab === 'action-items' && (
            <div className="p-5">
              <ActionItemsPanel
                mode="by_client"
                entityId={client.client_id}
                isCoach={true}
                clientName={client.name.split(' ')[0]}
              />
            </div>
          )}

          {/* ── Follow Ups tab ── */}
          {tab === 'followups' && (
            <FollowUpsTab
              coachClientId={Number(client.relationship_id)}
              coachToken={localStorage.getItem('authToken') || ''}
            />
          )}

          {/* ── Profile tab ── */}
          {tab === 'profile' && (
            <div className="p-5">
              {loadingProfile ? (
                <Spinner size="md" className="py-10" />
              ) : (
                <div className="space-y-6">

                  {/* Basic Info */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Basic Info</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="First Name" field="first_name" value={profile.first_name} onChange={handleFieldChange} />
                      <Field label="Last Name"  field="last_name"  value={profile.last_name}  onChange={handleFieldChange} />
                    </div>
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Email</label>
                        <p className="text-sm text-gray-500 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">{profile.email}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Phone</label>
                        <ReactPhoneInput
                          country="us"
                          value={profile.phone.replace(/^\+/, '')}
                          onChange={val => { setProfile(p => ({ ...p, phone: val ? `+${val}` : '' })); setProfileDirty(true); setPhoneError(null); }}
                          inputStyle={{ width: '100%', border: `1px solid ${phoneError ? '#ef4444' : '#d1d5db'}`, borderRadius: '0.5rem', fontSize: '0.875rem', height: '38px', color: '#111827' }}
                          buttonStyle={{ border: `1px solid ${phoneError ? '#ef4444' : '#d1d5db'}`, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem', background: '#f9fafb' }}
                          enableSearch
                        />
                        {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
                      </div>
                      <Field label="Date of Birth" field="date_of_birth" value={profile.date_of_birth} onChange={handleFieldChange} type="date" />
                    </div>
                    {profile.start_date && (
                      <p className="mt-3 text-xs text-gray-400">
                        Client since {new Date(profile.start_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </section>

                  {/* Emergency Contact */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Emergency Contact</h3>
                    <div className="space-y-3">
                      <Field label="Name"  field="emergency_contact_name"  value={profile.emergency_contact_name}  onChange={handleFieldChange} />
                      <Field label="Phone" field="emergency_contact_phone" value={profile.emergency_contact_phone} onChange={handleFieldChange} type="tel" />
                    </div>
                  </section>

                  {/* Address */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Address</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Country</label>
                        <select
                          value={profile.country}
                          onChange={e => { setProfile(p => ({ ...p, country: e.target.value, state_province: '' })); setProfileDirty(true); }}
                          className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="">Select country…</option>
                          {apiCountries.map(c => (
                            <option key={c.iso_code} value={c.iso_code}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <Field label="Street" field="street_address" value={profile.street_address} onChange={handleFieldChange} />
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="City" field="city" value={profile.city} onChange={handleFieldChange} />
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">State / Province</label>
                          {stateOptions.length > 0 ? (
                            <select
                              value={profile.state_province}
                              onChange={e => { setProfile(p => ({ ...p, state_province: e.target.value })); setProfileDirty(true); }}
                              className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            >
                              <option value="">Select…</option>
                              {stateOptions.map(s => (
                                <option key={s.code} value={s.code}>{s.name}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={profile.state_province}
                              onChange={e => handleFieldChange('state_province', e.target.value)}
                              className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            />
                          )}
                        </div>
                      </div>
                      <Field label="Postal Code" field="postal_code" value={profile.postal_code} onChange={handleFieldChange} />
                    </div>
                  </section>

                  {/* Background */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Background</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Marital Status" field="marital_status" value={profile.marital_status} onChange={handleFieldChange} />
                        <Field label="Dependents"     field="dependents"     value={profile.dependents}     onChange={handleFieldChange} />
                      </div>
                      <Field label="Occupation" field="occupation" value={profile.occupation} onChange={handleFieldChange} />
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Employer" field="employer" value={profile.employer} onChange={handleFieldChange} />
                        <Field label="Industry"  field="industry"  value={profile.industry}  onChange={handleFieldChange} />
                      </div>
                    </div>
                  </section>

                  {/* Bio */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Bio / Notes</h3>
                    <Field label="Bio" field="bio" value={profile.bio} onChange={handleFieldChange} multiline />
                  </section>

                  {/* Consent & Legal */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Consent & Legal</h3>
                    <div className="space-y-3">
                      {([
                        { key: 'ai_consent' as const, atKey: 'ai_consent_at' as const, label: 'AI session processing', desc: 'Consent to record, transcribe, and summarize sessions using AI' },
                        { key: 'privacy_policy_accepted' as const, atKey: 'privacy_policy_accepted_at' as const, label: 'Privacy Policy', desc: 'Client has read and accepted the Privacy Policy' },
                        { key: 'terms_accepted' as const, atKey: 'terms_accepted_at' as const, label: 'Terms of Use', desc: 'Client has read and accepted the Terms of Use' },
                      ] as { key: 'ai_consent' | 'privacy_policy_accepted' | 'terms_accepted'; atKey: 'ai_consent_at' | 'privacy_policy_accepted_at' | 'terms_accepted_at'; label: string; desc: string }[]).map(({ key, atKey, label, desc }) => {
                        const val = profile[key];
                        const at = profile[atKey];
                        return (
                          <div key={key} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">{label}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                              {at && (
                                <p className="text-xs text-gray-400 mt-1">
                                  {val ? 'Accepted' : 'Updated'} {new Date(at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => { setProfile(p => ({ ...p, [key]: true })); setProfileDirty(true); }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                                  val === true
                                    ? 'bg-green-100 border-green-300 text-green-800'
                                    : 'border-gray-200 text-gray-500 hover:border-green-300 hover:text-green-700'
                                }`}
                              >
                                ✓ Yes
                              </button>
                              <button
                                type="button"
                                onClick={() => { setProfile(p => ({ ...p, [key]: false })); setProfileDirty(true); }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                                  val === false
                                    ? 'bg-red-100 border-red-300 text-red-800'
                                    : 'border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-700'
                                }`}
                              >
                                ✗ No
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  {/* Save bar */}
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={handleSaveProfile}
                      disabled={!profileDirty || saving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {saving ? <Spinner size="sm" /> : saveSuccess ? <CheckIcon className="w-4 h-4" /> : null}
                      {saving ? 'Saving…' : saveSuccess ? 'Saved!' : 'Save Changes'}
                    </button>
                    {saveError && <p className="text-sm text-red-600">{saveError}</p>}
                  </div>

                  {/* ── Danger zone ── */}
                  <section className="border border-red-200 rounded-xl p-5 bg-red-50 mt-6">
                    <div className="flex items-start gap-3 mb-4">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold text-red-800">Delete Client</h3>
                        <p className="text-xs text-red-600 mt-1">
                          This permanently deletes <strong>{client.name}</strong>, all their appointments,
                          session notes, action items, and payment records. This cannot be undone.
                        </p>
                      </div>
                    </div>

                    {deleteStep === 'idle' && (
                      <button
                        onClick={() => setDeleteStep('confirm')}
                        className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 hover:bg-red-100 text-sm font-medium rounded-lg transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Delete this client
                      </button>
                    )}

                    {deleteStep === 'confirm' && (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-900 font-medium">
                          Type <strong className="text-red-700">{client.name}</strong> to confirm deletion:
                        </p>
                        <input
                          type="text"
                          value={deleteConfirmText}
                          onChange={e => setDeleteConfirmText(e.target.value)}
                          placeholder={client.name}
                          className="w-full text-sm text-gray-900 placeholder-gray-400 border border-red-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500 bg-white"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleDelete}
                            disabled={deleteConfirmText !== client.name || deleting}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            {deleting ? <Spinner size="sm" /> : <TrashIcon className="w-4 h-4" />}
                            {deleting ? 'Deleting…' : 'Permanently Delete'}
                          </button>
                          <button
                            onClick={() => { setDeleteStep('idle'); setDeleteConfirmText(''); setDeleteError(null); }}
                            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                        {deleteError && (
                          <p className="text-sm text-red-700 mt-2">{deleteError}</p>
                        )}
                      </div>
                    )}
                  </section>

                </div>
              )}
            </div>
          )}
          {/* ── AI Notes tab ── */}
          {tab === 'ai-notes' && (
            <div className="p-5 space-y-5">

              {/* Explanation card */}
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <SparklesIcon className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-violet-800">Client-specific note sections</p>
                    <p className="text-xs text-violet-700 mt-1">
                      Add extra sections that Claude will populate for <strong>{client.name}</strong> specifically.
                      These sections are <em>additive</em> — they append to your global Notes Settings
                      and apply to all past and future notes generated for this client.
                    </p>
                  </div>
                </div>

                {/* How it works */}
                <details className="mt-2">
                  <summary className="text-xs text-violet-600 font-medium cursor-pointer hover:text-violet-800 select-none">
                    How does this work? ↓
                  </summary>
                  <div className="mt-2 text-xs text-violet-700 space-y-1.5 pl-1">
                    <p>• Each section you add here becomes a new heading in the session notes for this client.</p>
                    <p>• <strong>Instructions</strong> tell Claude what to look for and document. Be specific — the more concrete your instruction, the better the output.</p>
                    <p>• <strong>Example:</strong> "Note any moments where I could have asked more open-ended coaching questions to explore this client's strengths. Flag specific transcript lines."</p>
                    <p>• Sections marked <strong>Client visible: Off</strong> (default) appear only in your coach view. Turn on visibility only if you want the client to see this section in their dashboard.</p>
                    <p>• Disabling a section preserves it in this list but skips it during generation — useful for temporarily pausing a section without losing it.</p>
                    <p>• These sections apply when you regenerate notes or upload a new transcript. Existing notes are <em>not</em> automatically regenerated.</p>
                  </div>
                </details>
              </div>

              {aiConfigLoading ? (
                <Spinner size="md" className="py-6" />
              ) : (
                <div className="space-y-4">

                  {extraSections.length === 0 && (
                    <p className="text-sm text-gray-400 italic text-center py-4">
                      No client-specific sections yet. Add one below.
                    </p>
                  )}

                  {extraSections.map((section, idx) => (
                    <div
                      key={section.id}
                      className={`border rounded-xl p-4 space-y-3 transition-opacity ${section.enabled ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50 opacity-60'}`}
                    >
                      {/* Section header row */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Section {idx + 1}
                        </span>
                        <div className="flex items-center gap-3">
                          {/* Enabled toggle */}
                          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none" title="When off, this section is skipped during generation but preserved here so you can re-enable it later.">
                            <input
                              type="checkbox"
                              checked={section.enabled}
                              onChange={e => updateExtraSection(section.id, 'enabled', e.target.checked)}
                              className="w-3.5 h-3.5 accent-violet-600"
                            />
                            Active
                          </label>
                          {/* Client visible toggle */}
                          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none" title="When on, clients can see this section in their dashboard. Default is off — coach-only.">
                            <input
                              type="checkbox"
                              checked={section.client_visible}
                              onChange={e => updateExtraSection(section.id, 'client_visible', e.target.checked)}
                              className="w-3.5 h-3.5 accent-violet-600"
                            />
                            <span className={section.client_visible ? 'text-violet-600 font-medium' : ''}>Client visible</span>
                          </label>
                          {/* Remove button */}
                          <button
                            onClick={() => removeExtraSection(section.id)}
                            className="text-gray-300 hover:text-red-400 transition-colors"
                            title="Remove this section"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Display name */}
                      <div>
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                          Section heading
                          <span className="ml-1 text-gray-300 normal-case font-normal">(shown as a heading in the notes)</span>
                        </label>
                        <input
                          type="text"
                          value={section.display_name}
                          onChange={e => updateExtraSection(section.id, 'display_name', e.target.value)}
                          placeholder="e.g. Strengths Exploration"
                          className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400"
                        />
                      </div>

                      {/* Instructions */}
                      <div>
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                          Instructions for Claude
                          <span className="ml-1 text-gray-300 normal-case font-normal">(what to look for and document)</span>
                        </label>
                        <textarea
                          rows={3}
                          value={section.instructions}
                          onChange={e => updateExtraSection(section.id, 'instructions', e.target.value)}
                          placeholder={`e.g. Note any moments where I could have asked more open-ended coaching questions to explore this client's strengths. Flag specific opportunities missed.`}
                          className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 resize-none"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          <InformationCircleIcon className="w-3.5 h-3.5 inline mr-0.5 align-text-bottom" />
                          Tip: Be specific. Reference what to flag, how many examples, and from whose perspective.
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Add section */}
                  <button
                    onClick={addExtraSection}
                    className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-800 font-medium transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add a section
                  </button>

                  {/* Save bar */}
                  <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                    <button
                      onClick={saveAiConfig}
                      disabled={aiConfigSaving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {aiConfigSaving ? <Spinner size="sm" /> : aiConfigSaved ? <CheckIcon className="w-4 h-4" /> : <SparklesIcon className="w-4 h-4" />}
                      {aiConfigSaving ? 'Saving…' : aiConfigSaved ? 'Saved!' : 'Save'}
                    </button>
                    {aiConfigError && <p className="text-sm text-red-600">{aiConfigError}</p>}
                  </div>

                  {/* Reminder */}
                  <p className="text-xs text-gray-400 pb-2">
                    Changes apply the next time notes are generated or a transcript is re-processed.
                    Existing session notes are not automatically updated.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Agreements tab ── */}
          {tab === 'agreements' && (
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Documents &amp; Agreements</h3>
                <button
                  onClick={async () => {
                    setSendWelcomeModal(true);
                    setWelcomeTitle('Welcome Packet');
                    setWelcomeSelectedIds(new Set());
                    setWelcomeSendError('');
                    setWelcomeSendSuccess('');
                    const [docs, tags] = await Promise.all([
                      listDocuments().catch(() => [] as DocumentItem[]),
                      listTemplateTags().catch(() => [] as DocumentTemplateTag[]),
                    ]);
                    setWelcomeDocs(docs);
                    setWelcomeCustomTags(tags);
                    setWelcomeTagValues(Object.fromEntries(tags.map(t => [t.key, t.default_value])));
                  }}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
                >
                  Send Docs
                </button>
              </div>
              {agreementsLoading ? <Spinner size="md" className="py-8" /> : agreementsError ? (
                <p className="text-sm text-red-600">{agreementsError}</p>
              ) : agreementPackets.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No documents sent yet.</p>
              ) : (
                <div className="space-y-3">
                  {agreementPackets.map(packet => (
                    <div key={packet.id} className="border border-gray-100 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{packet.title}</p>
                          <p className="text-xs text-gray-400">
                            Sent {packet.sent_at ? new Date(packet.sent_at).toLocaleDateString() : '—'}
                            {packet.signed_at && ` · Signed ${new Date(packet.signed_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          packet.status === 'signed' || packet.status === 'signed_offline'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {packet.status === 'signed' ? 'Signed' : packet.status === 'signed_offline' ? 'Sent & Signed Offline' : 'Pending'}
                        </span>
                      </div>
                      {packet.documents.length > 0 && (
                        <div className="divide-y divide-gray-50">
                          {packet.documents.map(doc => (
                            <div key={doc.document_id} className="flex items-center justify-between px-4 py-2">
                              <p className="text-xs text-gray-700">{doc.document_name}</p>
                              {doc.view_url && (
                                <a
                                  href={doc.view_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-600 hover:underline"
                                >
                                  View
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── Send Welcome Packet modal ── */}
      {sendWelcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-semibold text-gray-900">Send Documents to {client.name}</h3>
              <button onClick={() => setSendWelcomeModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Packet title</label>
                <input
                  type="text"
                  value={welcomeTitle}
                  onChange={(e) => setWelcomeTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select documents</label>
                {welcomeDocs.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No documents in library.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {welcomeDocs.map(doc => (
                      <label key={doc.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={welcomeSelectedIds.has(doc.id)}
                          onChange={() => setWelcomeSelectedIds(prev => {
                            const next = new Set(prev);
                            next.has(doc.id) ? next.delete(doc.id) : next.add(doc.id);
                            return next;
                          })}
                          className="rounded border-gray-300 text-primary-600"
                        />
                        <span className="text-sm text-gray-800">{doc.name}</span>
                        {doc.is_template && <span className="ml-auto text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">Template</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {welcomeCustomTags.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template tag values <span className="text-gray-400 font-normal">(override defaults for this send)</span>
                  </label>
                  <div className="space-y-2">
                    {welcomeCustomTags.map(tag => (
                      <div key={tag.key} className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono flex-shrink-0 w-36 truncate">{`{{${tag.key}}}`}</code>
                        <input
                          type="text"
                          value={welcomeTagValues[tag.key] ?? tag.default_value}
                          onChange={e => setWelcomeTagValues(prev => ({ ...prev, [tag.key]: e.target.value }))}
                          placeholder={tag.default_value || tag.label}
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {welcomeSendError && <p className="text-red-600 text-sm">{welcomeSendError}</p>}
              {welcomeSendSuccess && <p className="text-green-600 text-sm">{welcomeSendSuccess}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => setSendWelcomeModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >Cancel</button>
                <button
                  disabled={welcomeSending || welcomeSelectedIds.size === 0}
                  onClick={async () => {
                    if (!welcomeTitle.trim() || welcomeSelectedIds.size === 0) return;
                    setWelcomeSending(true);
                    setWelcomeSendError('');
                    try {
                      const nonEmptyTagValues = Object.fromEntries(
                        Object.entries(welcomeTagValues).filter(([, v]) => v.trim() !== '')
                      );
                      await createPacket(
                        client.relationship_id,
                        welcomeTitle.trim(),
                        Array.from(welcomeSelectedIds),
                        undefined,
                        Object.keys(nonEmptyTagValues).length > 0 ? nonEmptyTagValues : undefined,
                      );
                      setWelcomeSendSuccess('Sent!');
                      // Reload agreements if on that tab
                      if (tab === 'agreements') {
                        listClientFiles(Number(client.relationship_id)).then(setAgreementPackets).catch(() => {});
                      }
                      setTimeout(() => setSendWelcomeModal(false), 1500);
                    } catch (e: any) {
                      setWelcomeSendError(e.message || 'Send failed');
                    } finally {
                      setWelcomeSending(false);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                >
                  {welcomeSending ? 'Sending…' : 'Send to Client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {summaryAppt && (
        <SessionSummaryPanel
          appointmentId={summaryAppt.id}
          meetingName={summaryAppt.meeting_name}
          role="coach"
          clientName={client.name}
          coachClientId={client.relationship_id}
          onClose={() => setSummaryAppt(null)}
          onNavigateToCheckIns={() => { setSummaryAppt(null); setTab('checkins'); }}
        />
      )}

      {/* ── Transcript upload modal ── */}
      {transcriptAppt && (
        <>
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Upload Transcript</h3>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{transcriptAppt.meeting_name}</p>
                </div>
                <button
                  onClick={() => setTranscriptAppt(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 flex-1 overflow-y-auto space-y-4">
                {transcriptDone ? (
                  <div className="flex flex-col items-center py-8 gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckIcon className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">Summary generated!</p>
                    <p className="text-xs text-gray-500 text-center">
                      Transcript processed — session notes are ready. Click the notes icon to view.
                    </p>
                    <button
                      onClick={() => setTranscriptAppt(null)}
                      className="mt-2 text-sm bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Paste transcript text
                      </label>
                      <textarea
                        className="w-full h-52 text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none font-mono leading-relaxed"
                        placeholder="Speaker 1: Hello, how are you today?&#10;Speaker 2: I'm doing well, thanks..."
                        value={transcriptText}
                        onChange={e => setTranscriptText(e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1 text-right">{transcriptText.length.toLocaleString()} characters</p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex-1 border-t border-gray-100" />
                      <span className="text-xs text-gray-400">or</span>
                      <div className="flex-1 border-t border-gray-100" />
                    </div>

                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".txt,.vtt,.srt"
                        className="hidden"
                        onChange={handleTranscriptFile}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 border border-dashed border-gray-300 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg py-3 transition-colors"
                      >
                        <ArrowUpTrayIcon className="w-4 h-4" />
                        Upload .txt / .vtt / .srt file
                      </button>
                    </div>

                    {transcriptError && (
                      <p className="text-xs text-red-600 flex items-center gap-1.5">
                        <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                        {transcriptError}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              {!transcriptDone && (
                <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
                  <button
                    onClick={() => setTranscriptAppt(null)}
                    className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTranscriptSubmit}
                    disabled={!transcriptText.trim() || transcribing}
                    className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-40 flex items-center gap-2"
                  >
                    {transcribing && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                    {transcribing ? 'Generating summary…' : 'Generate Summary'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ClientDetailPanel;
